import type {GetServerSideProps, GetServerSidePropsContext} from 'next'
import {useRouter} from 'next/router'
import type {JSX} from 'react'
import type {PreviewModeData} from '@/services/kontent-ai/preview-mode/preview-mode-banner'
import {PreviewModeBanner} from '@/services/kontent-ai/preview-mode/preview-mode-banner'
import {handleError} from '@/services/handle-error'
import {KontentService as NewKontentService} from '@/services/kontent-ai/service'
import {BlockMapper} from '@/services/kontent-ai/mapper'
import {HeaderBlock} from '@/products/flexible-web-toolkit/components/header'
import type {
	GlobalConfigContentItem,
	AdvancedPageTemplateContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import {FooterBlock} from '@/products/flexible-web-toolkit/components/footer'
import {StickyBottomNavBlock} from '@/products/flexible-web-toolkit/blocks/sticky-bottom-nav'
import type {GlobalState} from '@/models/global-state.ts'
import {useAnalytics} from '@/utilities/analytics'
import type {TaxonomyListerContentItem} from '@/products/flexible-web-toolkit/blocks/listers/dynamic-article-lister'
import {env} from '@/services/env/server'
import type {Auth} from '@/products/auth/auth-provider'
import {GlobalClientState} from '@/services/global-client-state'
import {GeoLocationBlock} from '@/products/geolocation-popup/geo-location-popup' // Update this path to the correct location
import {SkipToMainContent} from '@/products/accessibility/skip-to-main-content'
import type {SiteCode} from '@/lib/pageContext/page-context-store'
import {
	preloadInitialArticleListerItems,
	preloadInitialCouponListerItems,
	preloadInitialDistributorCardListerItems,
	preloadInitialProductDirectoryListerItems,
	preloadInitialResourceCardListerItems,
} from '@/services/lister-items/page-preload'
import type {JsonLdGraph} from '@/schema/types'
import {getPageJsonLd} from '@/schema/get-page-json-ld'
import {JsonLd} from '@/components/json-ld'
import {isFibrPilotPage} from '@/services/fibr'

const TOWER_TO_SITE_CODE: Record<string, SiteCode> = {
	pet: 'PETP',
	vet: 'VETP',
	farm: 'FRMP',
	corporate: 'CORP',
}

export interface PageProps {
	page: Tersed<AdvancedPageTemplateContentItem>
	globalConfig: Tersed<GlobalConfigContentItem>
	template: string
	site: SiteCode
	locale: string
	auth: Auth
	isInPreviewMode: boolean
	previewData: PreviewModeData
	isOnTaxonomyTermPage: boolean
	reqUrl: string | undefined
	reqHost: string | undefined
	jsonLd: JsonLdGraph | null
	shouldRenderFibr: boolean
}

const DefaultPage = ({
	globalConfig,
	page,
	isInPreviewMode,
	previewData,
	jsonLd,
}: PageProps): JSX.Element => {
	const router = useRouter()
	useAnalytics(page, globalConfig)
	// TODO: better 404 handling
	if (router.locale === 'null') return <p>404 - Not found</p>

	const pageContext = {
		page,
		globalConfig,
	}

	const initialGlobalState: GlobalState = {
		articlesFilterOptions: {},
		couponsFilterOptions: {},
		productsFilterOptions: {},
		resourcesFilterOptions: {},
		faqSearchQuery: '',
	}

	// Check if we should show the geo-location popup
	// You can add your own logic here to determine when to show it
	return (
		<>
			{jsonLd ? <JsonLd graph={jsonLd} /> : null}
			<SkipToMainContent
				mainContentId="main-content"
				text={
					globalConfig.elements.skipToMainContent ||
					'Skip to main content'
				}
			/>
			<GlobalClientState initialGlobalState={initialGlobalState}>
				<PreviewModeBanner
					isInPreviewMode={isInPreviewMode}
					previewData={previewData}
				/>
				<GeoLocationBlock {...pageContext} BlockMapper={BlockMapper} />
				<HeaderBlock
					{...pageContext}
					BlockMapper={BlockMapper}
					block={{
						elements: {
							authenticationGateComponentUserRoles: [],
						},
						system: globalConfig.system,
					}}
				/>
				<main id="main-content">
					<BlockMapper
						blocks={page.elements.pageBlocks}
						{...pageContext}
					/>
				</main>
				<FooterBlock
					{...pageContext}
					BlockMapper={BlockMapper}
					block={{
						elements: {
							authenticationGateComponentUserRoles: [],
						},
						system: globalConfig.system,
					}}
				/>
				<StickyBottomNavBlock
					{...pageContext}
					BlockMapper={BlockMapper}
					block={{
						elements: {
							authenticationGateComponentUserRoles: [],
						},
						system:
							globalConfig.elements.stickyBottomNav[0]?.system ??
							globalConfig.system,
					}}
				/>
			</GlobalClientState>
		</>
	)
}

const parseParams = (
	params: GetServerSidePropsContext['params']
): {
	slug: string | string[] | undefined
	isLoggedIn: boolean
	isValidated: boolean
	isSelfValidated: boolean
} => {
	const slugArray = params?.slug as string[] | undefined
	if (!slugArray || slugArray.length < 3) {
		return {
			slug: params?.slug,
			isLoggedIn: false,
			isValidated: false,
			isSelfValidated: false,
		}
	}
	return {
		slug: slugArray.slice(0, -3),
		isLoggedIn: slugArray.slice(-3, -2)[0] === 'true',
		isValidated: slugArray.slice(-2, -1)[0] === 'true',
		isSelfValidated: slugArray.slice(-1)[0] === 'true',
	}
}

function parsePageNumber(pageQuery: string | string[] | undefined): number {
	const pageValue = Array.isArray(pageQuery) ? pageQuery[0] : pageQuery
	const pageNumber = Number(pageValue ?? '1')

	return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (
	context: GetServerSidePropsContext
) => {
	try {
		if (context.locale === 'null' || !context.locale)
			return {notFound: true}

		const {slug, isLoggedIn, isValidated, isSelfValidated} = parseParams(
			context.params
		)
		const pageNumber = parsePageNumber(context.query.page)

		if (!slug) {
			throw new Error('Slug is nullish')
		}

		const isInPreviewMode = context.preview ?? false
		const previewData: PreviewModeData =
			context.previewData &&
			(context.previewData as PreviewModeData).previewUrl
				? (context.previewData as PreviewModeData)
				: {previewUrl: ''}
		const parsedSlug = Array.isArray(slug)
			? slug.join('/')
			: slug.toString()
		const kontentService = NewKontentService.getInstance()
		const SENTINEL_LOCALES = ['testing', 'global', 'cee_english_master']
		let globalConfig =
			await kontentService.getContentItem<GlobalConfigContentItem>({
				locale: context.locale,
				usePreviewMode: isInPreviewMode,
				codename: env.GLOBAL_CONFIG_NAME,
			})

		if (
			!globalConfig.success &&
			SENTINEL_LOCALES.includes(context.locale)
		) {
			globalConfig =
				await kontentService.getContentItem<GlobalConfigContentItem>({
					locale: 'default',
					usePreviewMode: isInPreviewMode,
					codename: env.GLOBAL_CONFIG_NAME,
				})
		}

		if (!globalConfig.success) {
			throw new Error(JSON.stringify(globalConfig.error))
		}

		const isOnTaxonomyTermPage =
			context.resolvedUrl.includes('taxonomy/term')

		let page = await kontentService.getPage({
			locale: context.locale,
			urlSlug: isOnTaxonomyTermPage ? '/404' : parsedSlug, // Pull a 404 page as taxonomy page as a base
			usePreviewMode: isInPreviewMode,
		})

		if (!page.success) {
			const {error} = page
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				typeof (error as {code: number}).code === 'number'
			) {
				const code = (error as {code: number}).code

				if (code === 404) {
					context.res.statusCode = 404
					// redirect to 404 page
					page = await kontentService.getPage({
						locale: context.locale,
						urlSlug: '/404',
						usePreviewMode: isInPreviewMode,
					})

					if (!page.success && context.locale !== 'default') {
						page = await kontentService.getPage({
							locale: 'default',
							urlSlug: '/404',
							usePreviewMode: isInPreviewMode,
						})
					}

					if (!page.success) {
						return {
							notFound: true,
						}
					}
				} else {
					throw new Error(JSON.stringify(page.error))
				}
			} else {
				throw new Error(JSON.stringify(page.error))
			}
		}
		const urlArray = context.req.url ? context.req.url.split('/') : ['']
		const dupeLocale = context.locale === urlArray[1]
		const is404Page = context.resolvedUrl.includes('/404')
		if (is404Page || dupeLocale) {
			context.res.statusCode = 404
		}
		// "Taxonomy" pages are dynamically generated, so we build a lister here to display required cards
		if (isOnTaxonomyTermPage) {
			const tagCodename = parsedSlug
				.replaceAll('taxonomy/term/', '')
				.replaceAll('-', '_')

			const taxonomyLister: Tersed<TaxonomyListerContentItem> = {
				elements: {
					buttonText: '',
					tags: [
						{
							codename: tagCodename,
							name: 'Play',
						},
					],
					columns: 3,
					featuredDescription: '',
					featuredTitle: '',
					initialArticleNumber: 6,
					listerDescription: '',
					listerTitle: '',
					showFeatured: [],
					subsequentArticleNumber: 6,
					titleDescSpacing: [],
					usePagination: [
						{
							codename: 'yes',
							name: 'Yes',
						},
					],
					authenticationGateComponentUserRoles: [],
				},
				system: {
					codename: 'dynamic_article_lister',
					id: 'taxonomy',
					name: 'Taxonomy Lister',
					type: 'dynamic_article_lister',
				},
			}

			page.data.elements.pageBlocks = [taxonomyLister]
			page.data.elements.pageText = ''
			context.res.statusCode = 200
		}

		if (!isLoggedIn) {
			await preloadInitialArticleListerItems({
				locale: context.locale,
				page: page.data,
				pageNumber,
				preview: isInPreviewMode,
			})
			await preloadInitialProductDirectoryListerItems({
				locale: context.locale,
				page: page.data,
				pageNumber,
				preview: isInPreviewMode,
			})
			await preloadInitialResourceCardListerItems({
				locale: context.locale,
				page: page.data,
				pageNumber,
				preview: isInPreviewMode,
			})
			await preloadInitialCouponListerItems({
				locale: context.locale,
				page: page.data,
				preview: isInPreviewMode,
			})
			await preloadInitialDistributorCardListerItems({
				locale: context.locale,
				page: page.data,
				preview: isInPreviewMode,
			})
		}

		// Skip JSON-LD for taxonomy pages — they load /404 as a base page so
		// page.data.elements.pageUrl would be incorrect (points to 404, not the taxonomy URL).
		const jsonLd: JsonLdGraph | null = isOnTaxonomyTermPage
			? null
			: getPageJsonLd(context, page.data, globalConfig.data)
		const shouldRenderFibr = isFibrPilotPage({
			tower: env.NEXT_PUBLIC_TOWER,
			locale: context.locale,
			pageUrl: page.data.elements.pageUrl,
			featuresList: globalConfig.data.elements.features,
		})

		return {
			props: {
				page: page.data,
				globalConfig: globalConfig.data,
				template: page.data.system.type,
				site: TOWER_TO_SITE_CODE[env.NEXT_PUBLIC_TOWER] ?? 'PETP',
				locale: context.locale,
				isInPreviewMode,
				previewData,
				auth: {
					isLoggedIn,
					isValidated,
					isSelfValidated,
				},
				isOnTaxonomyTermPage,
				reqUrl: context.req.url,
				reqHost: context.req.headers.host,
				jsonLd,
				shouldRenderFibr,
			},
		}
	} catch (error) {
		handleError(JSON.stringify(error), __filename, 'Server error')
		throw error
	}
}

export default DefaultPage
